import { Users } from 'lucide-react'
import type { User } from '@/types/nodes'
import { cn } from '@/lib/utils/cn'

/**
 * Icon size variants for user count display
 */
type IconSize = 'sm' | 'md' | 'lg'

const iconSizeClasses: Record<IconSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
}

/**
 * Display modes for the user count component
 */
type DisplayMode = 'icon-count' | 'count-only' | 'icon-only'

/**
 * Props for the NodeUserCount component
 */
interface NodeUserCountProps {
  /**
   * Array of users to count (mutually exclusive with count)
   */
  users?: User[]
  /**
   * Direct count value (mutually exclusive with users)
   */
  count?: number
  /**
   * Size of the user icon (defaults to 'sm')
   */
  iconSize?: IconSize
  /**
   * Display mode for the component (defaults to 'icon-count')
   */
  displayMode?: DisplayMode
  /**
   * Optional label to display before the count (defaults to empty string)
   */
  label?: string
  /**
   * Additional CSS classes for the container
   */
  className?: string
  /**
   * Whether to use hover mode (icon only, count reveals on hover)
   */
  hoverMode?: boolean
  /**
   * Whether to show the count as a badge instead of plain text
   */
  showAsBadge?: boolean
  /**
   * Custom badge variant when showAsBadge is true
   */
  badgeVariant?: 'default' | 'secondary' | 'outline'
}

/**
 * Get user count from users array or direct count
 */
function getUserCount(users?: User[], count?: number): number {
  if (count !== undefined) {
    return count
  }
  return users?.length || 0
}

/**
 * A reusable component for displaying user counts with consistent styling
 * Works with either an array of users or a direct count value
 *
 * @example
 * ```tsx
 * // Basic usage with users array
 * <NodeUserCount users={project.users} />
 *
 * // With direct count
 * <NodeUserCount count={5} />
 *
 * // Count only mode
 * <NodeUserCount users={users} displayMode="count-only" />
 *
 * // Icon only mode
 * <NodeUserCount users={users} displayMode="icon-only" />
 *
 * // With custom label
 * <NodeUserCount users={users} label="Members:" />
 *
 * // With custom styling
 * <NodeUserCount
 *   users={users}
 *   iconSize="lg"
 *   className="text-lg"
 * />
 *
 * // As a badge
 * <NodeUserCount
 *   users={users}
 *   showAsBadge
 *   badgeVariant="secondary"
 * />
 *
 * // Hover mode (icon only, count reveals on hover)
 * <NodeUserCount users={users} hoverMode />
 *
 * // Hover mode with custom label
 * <NodeUserCount
 *   users={users}
 *   label="Team size:"
 *   hoverMode
 * />
 * ```
 */
export function NodeUserCount({
  users,
  count,
  iconSize = 'sm',
  displayMode = 'icon-count',
  label = '',
  className = '',
  hoverMode = false,
  showAsBadge = false,
  badgeVariant = 'secondary',
}: NodeUserCountProps) {
  const userCount = getUserCount(users, count)

  // Don't render if count is 0 and not showing as badge (unless explicitly showing empty state)
  if (userCount === 0 && !showAsBadge) {
    return null
  }

  const shouldShowIcon = displayMode === 'icon-count' || displayMode === 'icon-only'
  const shouldShowCount = displayMode === 'icon-count' || displayMode === 'count-only'

  const content = (
    <div className={`flex items-center gap-1 ${className}`}>
      {label && <span className="text-xs text-muted-foreground">{label}</span>}
      <div className="flex items-center gap-1">
        {shouldShowIcon && <Users className={cn(iconSizeClasses[iconSize], 'flex-shrink-0')} />}
        {shouldShowCount &&
          (showAsBadge ? (
            <span
              className={cn(
                'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                badgeVariant === 'secondary' && 'bg-secondary text-secondary-foreground',
                badgeVariant === 'outline' && 'border border-border bg-background',
                badgeVariant === 'default' && 'bg-primary text-primary-foreground'
              )}
            >
              {userCount}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground font-medium">{userCount}</span>
          ))}
      </div>
    </div>
  )

  if (hoverMode) {
    return (
      <div className="group relative">
        <div className={cn('flex items-center gap-1', className)}>
          {label && <span className="text-xs text-muted-foreground">{label}</span>}
          {shouldShowIcon && <Users className={cn(iconSizeClasses[iconSize], 'flex-shrink-0')} />}
        </div>
        <div className="absolute left-0 top-full mt-1 opacity-0 group-hover:opacity-100 transition-opacity bg-popover text-popover-foreground text-xs px-2 py-1 rounded shadow-md z-10">
          {userCount} user{userCount !== 1 ? 's' : ''}
        </div>
      </div>
    )
  }

  return content
}
